
import {
    CategoryModel,
    CategoryUserModel,
    TenantUserLocationModel,
    LocationModel,
    RoleModel,
    TenantInvitationRoleModel,
    TenantModel,
    TenantUserModel,
} from '@/libs/Api';
import Page from '@/Page.vue';
import { Guid } from '@/libs/common/functions/guid';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { tenantsStore, tenantUsersStore } from '@/libs/tenants/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { rolesStore } from '@/libs/roles/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import ApiService from '@/libs/core/api-service';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { categoriesStore } from '@/libs/categories/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';

@Component({})
export default class InviteAccountDialog extends Page {
    dialog = false;
    valid = true;
    saving = false;
    showInvitationRoleRules = false;
    showToastExternalLocationError = false;
    email = '';
    userName = '';
    password = '';
    firstName = '';
    employeeId = '';
    departmentId: string | null = null;
    positionId: string | null = null;
    categoryId: string | null = null;
    locale: string | null = "cs";
    lastName = '';
    phoneNumber = '';
    pin = '';
    useQuickLogin = false;
    isExternal = false;
    accessAllCategories = true;
    loginType = 'email';
    invitationRoles: TenantInvitationRoleModel[] = [];
    allowedCategories: CategoryUserModel[] = [];
    allowedLocations: TenantUserLocationModel[] = [];
    roleId: string | null = null;
    locationId: string | null = null;

    pinRules = [
        (v: any) => !!v || this.$t('validation.thisFieldIsRequired'),
        (v: string) => (v && v.length <= 4) || this.$t('validation.max4numbers'),
        (v: string) => (v && /^[0-9]+$/gi.test(v)) || this.$t('validation.onlyNumbers'),
    ];

    protected get employeeIdRules() {
        const tenant = tenantsStore.useGetter(CrudGetter.Detail) as TenantModel;
        return [
            (v: string) =>
                !v ||
                (v && v.toString().length <= tenant.quickLoginPersonalNumberPositionCount!) ||
                this.$t('validation.maxNumbers', { value: tenant.quickLoginPersonalNumberPositionCount! }),
        ];
    }

    get rules() {
        return [
            (v: any) =>
                !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$i18n.t('validation.emailIsRequired'),
        ];
    }

    get userNameRules() {
        return [(v: any) => !v || /^[a-zA-Z0-9.]{6,}$/.test(v) || this.$i18n.t('validation.userNameIsRequried')];
    }

    get passwordRules() {
        return [(v: any) => !!v || this.$i18n.t('validation.thisFieldIsRequired')];
    }

    get requiredRules() {
        return [(v: any) => !!v || this.$i18n.t('validation.thisFieldIsRequired')];
    }

    get activeRoles(): string[] {
        return this.invitationRoles?.map((x) => x.roleId!) ?? [];
    }

    get isSingleTenant(): boolean {
        return authStore.useGetter(AuthGetter.GetIsSingleTenant) ?? false;
    }

    get useTpm(): boolean {
        return (window as any).UseTpm;
    }

    @Watch('dialog')
    dialogChanged(v: boolean) {
        if (!v) this.close();
    }

    @Ref() form!: any;
    @Prop() tenantId!: string;

    open() {
        this.invitationRoles = [];
        this.email = '';
        this.userName = '';
        this.password = '';
        this.firstName = '';
        this.lastName = '';
        this.phoneNumber = '';
        this.employeeId = '';
        this.pin = '';
        this.departmentId = null;
        this.positionId = null;
        this.categoryId = null;
        this.accessAllCategories = true;
        this.allowedCategories = [];
        this.allowedLocations = [];
        this.useQuickLogin = false;
        this.loginType = 'email';
        this.dialog = true;
        this.showInvitationRoleRules = false;
        this.isExternal = false;
        this.locationId = null;
    }

    close() {
        this.dialog = false;
    }

    save() {
        this.showInvitationRoleRules = false;
        if (this.form.validate() && this.invitationRoles.length > 0) {
            if (this.isExternal && (!this.allowedLocations || this.allowedLocations.length === 0)) {
                this.showToastExternalLocationError = true;
                return;
            }
            const InviteTenantUserModel: TenantUserModel = {
                id: '00000000-0000-0000-0000-000000000000',
                user: {
                    id: '00000000-0000-0000-0000-000000000000',
                    email: this.loginType == 'email' ? this.email : null,
                    userName: this.loginType == 'userName' ? this.userName : null,
                    firstName: this.loginType == 'userName' ? this.firstName : null,
                    lastName: this.loginType == 'userName' ? this.lastName : null,
                    phoneNumber: this.loginType == 'userName' ? this.phoneNumber : null,
                    language: this.locale
                },
                userRoles: this.invitationRoles,
                pwRequest: this.loginType == 'userName' ? this.password : null,
                employeeId: this.loginType == 'userName' ? this.employeeId : null,
                accessAllCategories: this.accessAllCategories,
                categories: this.allowedCategories,
                isExternal: this.isExternal,
                locations: this.allowedLocations,
                departmentId: this.departmentId,
                positionId: this.positionId
            };
            tenantUsersStore.dispatch(CrudAction.Create, { item: InviteTenantUserModel });
            this.saving = true;
            this.subscribe(tenantUsersStore, CrudReponse.Create)
                .then((e: TenantUserModel) => {
                    if (this.loginType == 'userName' && this.useQuickLogin && this.pin != '' && this.pin != null) {
                        ApiService.api
                            .setQuickLoginPin(e.id!, this.pin)
                            .then(() => {
                                this.close();
                                this.saving = false;
                                this.$emit('change');
                            })
                            .catch(() => {
                                this.close();
                                this.saving = false;
                                this.$emit('change');
                            });
                    } else {
                        this.close();
                        this.saving = false;
                        this.$emit('change');
                    }
                })
                .catch(() => {
                    this.close();
                    this.saving = false;
                    this.$emit('change');
                });
        } else {
            this.showInvitationRoleRules = true;
        }
    }

    addRoleClick() {
        const roles = rolesStore.useGetter(CrudGetter.Data) as RoleModel[];
        const role = roles.firstOrDefault((x) => x.id == this.roleId);
        if (role) {
            this.invitationRoles!.push({
                id: Guid.EmptyGuid(),
                roleId: role.id,
                role: role,
            } as TenantInvitationRoleModel);
        }
        this.roleId = null;
    }

    addAllowedCategoryClick() {
        const categories = (categoriesStore.useGetter(CrudGetter.Data) as CategoryModel[]) ?? [];
        const category = categories.firstOrDefault((x) => x.id == this.categoryId);
        if (category) {
            this.allowedCategories.push({
                id: Guid.EmptyGuid(),
                categoryId: this.categoryId,
                category: category,
            } as CategoryUserModel);
        }
        this.categoryId = null;
    }

    deleteCategoryClick(index: number) {
        this.allowedCategories.splice(index, 1);
    }

    addLocationClick() {
        if (!this.locationId) return;
        const exists = (this.allowedLocations ?? []).some((x) => x.locationId == this.locationId);
        if (!exists) {
            this.allowedLocations.push({
                id: Guid.EmptyGuid(),
                locationId: this.locationId,
            } as TenantUserLocationModel);
        }
        this.locationId = null;
    }

    deleteLocationClick(index: number) {
        this.allowedLocations.splice(index, 1);
    }

    getLocationName(locationId?: string | null): string {
        if (!locationId) return '';
        const all: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const found: LocationModel | undefined = (all as any).firstOrDefault
            ? (all as any).firstOrDefault((x: LocationModel) => x.id == locationId)
            : all.find((x) => x.id == locationId);
        return found?.name ?? '';
    }
}
